import type { Country } from '@acs/shared';
import { Banner, Field, Select } from '@acs/ui';
import { useI18n } from '../../i18n/index.js';
import type { TranslationKey } from '../../i18n/index.js';

/**
 * Per-operator cost of diverting a mobile to a same-country local number (doc 13a).
 *
 * Forwarding is billed as an outbound national call from the owner's own line —
 * no operator charges a separate "forwarding fee". What differs, and what this
 * table is for, is whether the owner's *package* pays for that call. "Just divert
 * your line, it's free" is NOT true for:
 *   • PL Orange — the cennik states that nielimitowane rozmowy "nie obejmują …
 *     przekierowań połączeń", so every diverted call is metered at 0,29 zł/min.
 *     Subscription only: the service does not exist on Orange na kartę.
 *   • PL Plus — forwarding is a priced usługa dodatkowa charged per started
 *     30 seconds: 0,24 zł to Plus and landlines, 0,73 zł to other mobile
 *     networks. Our pool number sits in a P4 range, i.e. the expensive column
 *     — ≈1,46 zł/min. Subscription only; on prepaid Plus documents forwarding
 *     to its own voicemail and nothing else.
 *   • DE O2  — excludes permanent forwarding ("Dauernutzung") from the flat.
 *   • FR Bouygues — €0.50/min to any non-Bouygues number (i.e. to our number).
 *   • FR Free — €0.05/min metered per forwarded minute.
 *
 * The two PL operators that really are free say so in opposite ways: T-Mobile
 * spells the inclusion out (Nielimitowane minuty "obejmuje … przekierowania
 * połączeń"), while Play simply never excludes them — its current SOLO/GRUPA
 * cenniki exclude only special and international numbers. Play is also the only
 * PL operator whose prepaid can divert at all (0,79 zł/min, needs credit);
 * Orange and Plus refuse forwarding on prepaid outright.
 *
 * PL rechecked 2026-09-24 against the operators' own cenniki. Each row's
 * `priceHref` is the document its number comes from, so a re-check starts there.
 *
 * Number portability means the operator CANNOT be derived from the number's prefix,
 * so the owner *picks* their operator (stored as `ownerLineOperator`); that choice —
 * not the number, which we don't keep — drives the warning. Each row carries two
 * links: the operator's how-to page and the document its price comes from. In
 * DE/FR they are the same page, because there the help page states the price too.
 */
type Tone = 'ok' | 'warn' | 'metered' | 'bad';

interface OperatorCost {
  /** Stable id persisted in `business.ownerLineOperator`. */
  id: string;
  /** Operator plus its low-cost sub-brands, so owners recognise their carrier. */
  name: string;
  /** One line on what a divert to our (local) number costs them — a catalogue
   *  key, so the pricing table stays pure data (doc 20 D8). */
  noteKey: TranslationKey;
  /** ok = included on normal plans; warn = not guaranteed, check it;
   *  metered = definitely paid, per minute; bad = paid and expensive. */
  tone: Tone;
  /** The operator's own how-to page for forwarding. Absent when there is none:
   *  Play documents the service only for its landline product, and pointing a
   *  mobile owner at that page would be worse than pointing them nowhere. */
  guideHref?: string;
  /** The official document the note's price comes from — an owner can see the
   *  row themselves, and so can whoever re-checks these numbers next. */
  priceHref: string;
  /** Operator-specific dial codes, when they deviate from the GSM standard.
   * None do in FR/DE/PL today — the field exists so a future exception is a
   * data change rather than a rewrite. */
  codes?: Partial<Record<DivertKind, string>>;
}

export type DivertKind = 'all' | 'noAnswer' | 'busy' | 'unreachable' | 'cancel';

/**
 * GSM supplementary-service codes — the same on every network in our launch
 * countries, which is why they're a shared default rather than per-operator
 * data. `%s` is the forwarding target. Dialled from the business handset.
 */
const STANDARD_CODES: Record<DivertKind, string> = {
  all: '**21*%s#',
  noAnswer: '**61*%s#',
  busy: '**67*%s#',
  unreachable: '**62*%s#',
  cancel: '##002#',
};

export interface DivertOption {
  kind: DivertKind;
  /** Tab label — short enough to sit in a strip of five without wrapping. */
  tab: string;
  /** The choice, as the owner would describe it to a colleague. */
  label: string;
  /** What this choice means for the owner, in plain language. */
  description: string;
  /** Ready to dial, with the target number already substituted. */
  code: string;
}

/** The three catalogue keys describing one divert. */
export const divertKeys = (kind: DivertKind) => ({
  tab: `divert.${kind}.tab` as TranslationKey,
  label: `divert.${kind}.label` as TranslationKey,
  description: `divert.${kind}.description` as TranslationKey,
});

/**
 * Tab order — and therefore also the default, since a tab strip lands on its
 * first tab.
 *
 * "Calls you miss" leads. It used to be "Every call", on the reasoning that an
 * owner setting up an AI receptionist means to hand the line over — but that is
 * the setting with the most to lose if it is wrong, because the owner's phone
 * stops ringing and they find out from a customer. Diverting only unanswered
 * calls changes nothing the owner can see until they let one ring out, which
 * makes it the right thing to land on: the cautious default, with handing the
 * whole line over one tab away for owners who want it.
 *
 * "Every call" therefore sits second, the two situational diverts after it, and
 * "Turn off" last — it is the undo, and it belongs where an undo belongs.
 *
 * This replaced a primary/secondary split that put two of these in a toggle and
 * hid the other three behind a disclosure. The split was there to keep an owner
 * from having to compare four GSM codes at once; tabs solve the same problem
 * better, because only one code is ever on screen and none of the choices is
 * buried.
 */
export const DIVERT_ORDER = ['noAnswer', 'all', 'busy', 'unreachable', 'cancel'] as const;

/** The dial string for one divert, with `phoneNumber` already substituted. */
export function divertCode(
  kind: DivertKind,
  operatorId: string | null,
  country: Country,
  phoneNumber: string,
): string {
  const operator = OPERATORS[country].find((o) => o.id === operatorId);
  return (operator?.codes?.[kind] ?? STANDARD_CODES[kind]).replace('%s', phoneNumber);
}

/** One ready-to-show divert: its wording plus its dial code. The translator is
 *  passed in so callers stay in charge of the locale and this file needs no
 *  React context to be testable. */
export function divertOption(
  kind: DivertKind,
  operatorId: string | null,
  country: Country,
  phoneNumber: string,
  t: (key: TranslationKey) => string,
): DivertOption {
  const keys = divertKeys(kind);
  return {
    kind,
    tab: t(keys.tab),
    label: t(keys.label),
    description: t(keys.description),
    code: divertCode(kind, operatorId, country, phoneNumber),
  };
}

/** Every divert, ready to show, in {@link DIVERT_ORDER}. */
export function divertOptions(
  operatorId: string | null,
  country: Country,
  phoneNumber: string,
  t: (key: TranslationKey) => string,
): DivertOption[] {
  return DIVERT_ORDER.map((kind) => divertOption(kind, operatorId, country, phoneNumber, t));
}

const OPERATORS: Record<Country, OperatorCost[]> = {
  PL: [
    {
      id: 'pl-orange',
      name: 'Orange',
      noteKey: 'operator.note.plOrange',
      tone: 'metered',
      guideHref: 'https://www.orange.pl/poradnik/na-czym-polega-przekierowanie-polaczen-i-jak-je-wlaczyc/',
      // Cennik usług w ofercie Abonament komórkowy III (od 22.04.2026) holds
      // both halves of the note: przekierowania excluded from nielimitowane
      // rozmowy, and the 0,29 zł/min domestic rate that then applies.
      priceHref: 'https://dokumenty.orange.pl/bw/dokumenty/20155/0/117724/4786bd50-ca1f-4679-b9f5-77557cfc21db',
    },
    {
      id: 'pl-play',
      name: 'Play',
      noteKey: 'operator.note.plPlay',
      tone: 'ok',
      // No guideHref — see the field's own comment. The cennik row reads
      // "Przekierowanie pozostałych połączeń przychodzących — jak za połączenie
      // z numerem na który nastąpi przekierowanie", i.e. the plan's own rate.
      priceHref:
        'https://www.play.pl/dokumenty/2026/157531/Cennik_Uslug_poza_abonamentem_w_Taryfie_GRUPA_i_SOLO_2026-01-03.pdf',
    },
    {
      id: 'pl-plus',
      name: 'Plus',
      noteKey: 'operator.note.plPlus',
      tone: 'bad',
      guideHref: 'https://bok.plus.pl/article/535',
      // The index rather than one cennik: the per-30-s price differs by tariff
      // (0,24–0,73 zł with VAT), so the owner needs their own tariff's file.
      priceHref: 'https://www.plus.pl/regulaminy-i-cenniki',
    },
    {
      id: 'pl-tmobile',
      name: 'T-Mobile',
      noteKey: 'operator.note.plTmobile',
      tone: 'ok',
      guideHref: 'https://www.t-mobile.pl/informacje-i-pomoc/pomoc-techniczna/aktywne-przekierowania-polaczen',
      // Regulamin i cennik oferty abonament komórkowy — the Nielimitowane
      // minuty clause that names przekierowania as included.
      priceHref:
        'https://www.t-mobile.pl/c/_bffapi/webcms-proxy/v1/plar/documents/6830725a7186692775bd0e3b/attachments/ec733dee001d4577853079db9c36538a',
    },
  ],
  DE: [
    {
      id: 'de-telekom',
      name: 'Telekom (MagentaMobil)',
      noteKey: 'operator.note.telekomFlat',
      tone: 'ok',
      guideHref: 'https://www.telekom.de/hilfe/mobilfunk/telefonieren-sms-mms/kosten-rufumleitung',
      priceHref: 'https://www.telekom.de/hilfe/mobilfunk/telefonieren-sms-mms/kosten-rufumleitung',
    },
    {
      id: 'de-vodafone',
      name: 'Vodafone',
      noteKey: 'operator.note.allnetFlat',
      tone: 'ok',
      guideHref: 'https://www.vodafone.de/featured/service-hilfe/android-tipps/android-rufumleitung-einrichten-so-gehts-schritt-fuer-schritt/',
      priceHref: 'https://www.vodafone.de/featured/service-hilfe/android-tipps/android-rufumleitung-einrichten-so-gehts-schritt-fuer-schritt/',
    },
    {
      id: 'de-o2',
      name: 'O₂ / Telefónica',
      noteKey: 'operator.note.o2',
      tone: 'warn',
      guideHref: 'https://hilfe.o2online.de/o2-mobilfunk-vertrag-tarife-15/kosten-rufumleitung-in-das-festnetz-oder-in-das-o2-netz-568952',
      priceHref: 'https://hilfe.o2online.de/o2-mobilfunk-vertrag-tarife-15/kosten-rufumleitung-in-das-festnetz-oder-in-das-o2-netz-568952',
    },
  ],
  FR: [
    {
      id: 'fr-orange',
      name: 'Orange / Sosh',
      noteKey: 'operator.note.noSurcharge',
      tone: 'ok',
      guideHref: 'https://assistance.orange.fr/nid/172736',
      priceHref: 'https://assistance.orange.fr/nid/172736',
    },
    {
      id: 'fr-sfr',
      name: 'SFR / RED',
      noteKey: 'operator.note.noSurcharge',
      tone: 'ok',
      guideHref: 'https://assistance.sfr.fr',
      priceHref: 'https://assistance.sfr.fr',
    },
    {
      id: 'fr-free',
      name: 'Free Mobile',
      noteKey: 'operator.note.freeMetered',
      tone: 'warn',
      guideHref: 'https://assistance.free.fr/articles/1755',
      priceHref: 'https://assistance.free.fr/articles/1755',
    },
    {
      id: 'fr-bouygues',
      name: 'Bouygues / B&You',
      noteKey: 'operator.note.bouygues',
      tone: 'bad',
      guideHref: 'https://www.assistance.bouyguestelecom.fr/s/article/activation-desactivation-renvoi-appel',
      priceHref: 'https://www.assistance.bouyguestelecom.fr/s/article/activation-desactivation-renvoi-appel',
    },
  ],
};

const countryAdjectiveKey = (c: Country) => `operator.countryAdjective.${c}` as TranslationKey;
const toneLeadKey = (tone: Tone) => `operator.tone.${tone}` as TranslationKey;

/** The operators we show for a country (for tests / reuse). */
export function operatorsFor(country: Country): readonly OperatorCost[] {
  return OPERATORS[country];
}

/** The operator's own how-to page — carrier apps are the alternative to
 *  dialling. Null when the operator publishes none, which also hides the
 *  sentence that would have linked to it. */
export function operatorHelp(country: Country, id: string | null): { name: string; href: string } | null {
  const op = OPERATORS[country].find((o) => o.id === id);
  return op?.guideHref ? { name: op.name, href: op.guideHref } : null;
}

function OperatorLine({ op }: { op: OperatorCost }) {
  const { t } = useI18n();
  return (
    <>
      {t(op.noteKey)}{' '}
      <a href={op.priceHref} target="_blank" rel="noopener noreferrer">
        {t('operator.pricingLink')}
      </a>
    </>
  );
}

/**
 * "What will forwarding cost me?" — folded away by default.
 *
 * For most operators the answer is still "nothing", so this used to be a
 * paragraph, a select, a badge and a comparison table sitting permanently
 * between the owner and the one thing they came here to do. It is
 * now a disclosure whose summary carries the answer, and it opens itself when
 * the operator they picked is one of the exceptions — the only case where the
 * cost is load-bearing.
 */
export function ForwardingCost({
  country,
  selectedId,
  onSelect,
}: {
  country: Country;
  selectedId: string | null;
  onSelect: (id: string) => void;
}) {
  const { t } = useI18n();
  const operators = OPERATORS[country];
  const selected = operators.find((o) => o.id === selectedId);
  const notable = selected && selected.tone !== 'ok';

  return (
    <>
      {/* Hoisted out of the disclosure: an owner on Bouygues or Plus needs to
          see the per-minute price without opening anything. `warn` stays on the
          quieter `info` tone — it means "we can't promise", not "this costs". */}
      {notable && (
        <Banner tone={selected.tone === 'warn' ? 'info' : 'warning'}>
          <strong>
            {t('operator.leadIn', { operator: selected.name, lead: t(toneLeadKey(selected.tone)) })}
          </strong>{' '}
          <OperatorLine op={selected} />
        </Banner>
      )}

      <details className="fwd-details">
        <summary className="fwd-details__summary">
          <span>{t('operator.costSummary')}</span>
          <span className="fwd-details__meta">
            {selected
              ? t('operator.costMeta', { operator: selected.name, lead: t(toneLeadKey(selected.tone)) })
              : t('operator.tone.ok')}
          </span>
        </summary>
        <div className="fwd-details__body">
          <p className="muted" style={{ fontSize: 13, marginTop: 0 }}>
            {t('operator.costExplainer', { country: t(countryAdjectiveKey(country)) })}
          </p>

          <Field label={t('operator.yourOperator')}>
            <Select value={selectedId ?? ''} onChange={(e) => onSelect(e.target.value)}>
              <option value="" disabled>
                {t('operator.selectPrompt')}
              </option>
              {operators.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.name}
                </option>
              ))}
            </Select>
          </Field>

          <ul className="fwd-operators">
            {operators.map((op) => (
              <li key={op.id} className={op.id === selectedId ? 'is-selected' : undefined}>
                <strong>{op.name}</strong>
                <p className="muted" style={{ fontSize: 13, margin: '2px 0 0' }}>
                  <OperatorLine op={op} />
                </p>
              </li>
            ))}
          </ul>
        </div>
      </details>
    </>
  );
}
